import React, { useState, isValidElement } from 'react';
import { connect } from 'react-redux';
import * as XLSX from 'xlsx';
import { ColumnType } from './constants';
import { constructQuery } from './dataActions';
import { fetchM } from '../../utils/fetch';
import { getDecodedUser } from '../../utils/login';
import { isInternal } from '../../agenda/Cro/CroUtils';
import Button from '../form/Button';
import { FormattedDate } from 'react-intl';

const formatValue = (value, column) => {
    if (value === null || value === undefined) {
        return '';
    }

    switch (column.type) {
        case ColumnType.DATE: {
            const d = new Date(value);
            if (isNaN(d.getTime())) return String(value);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}. ${month}. ${year}`;
        }

        case ColumnType.DATETIME:
        case ColumnType.DATETIMEWITHOUTZONE: {
            const d = new Date(value);
            if (isNaN(d.getTime())) return String(value);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${day}. ${month}. ${year}, ${hours}:${minutes}`;
        }

        case ColumnType.CHECK:
        case ColumnType.YESNO:
            return value ? 'ANO' : 'NE';

        case ColumnType.NUMBER:
            return value;

        case ColumnType.TEXT:
        case ColumnType.ENUM:
        default:
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
};

const getFieldValue = (row, field) => {
    if (!field) return null;
    return field.split('.').reduce(
        (obj, key) => (obj && obj[key] !== undefined ? obj[key] : null),
        row
    );
};

const extractText = (node) => {
    if (node === null || node === undefined || node === false || node === true) {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractText).filter(Boolean).join(' ');
    }
    if (isValidElement(node)) {
        if (node.type === FormattedDate && node.props && node.props.value !== undefined) {
            const d = new Date(node.props.value);
            if (!isNaN(d.getTime())) {
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                return `${day}. ${month}. ${d.getFullYear()}`;
            }
            return '';
        }
        return extractText(node.props && node.props.children);
    }
    return '';
};

const getCellValue = (row, column) => {
    if (typeof column.exportValue === 'function') {
        return column.exportValue(row, column);
    }

    if (column.type === ColumnType.JSX && typeof column.jsx === 'function') {
        return extractText(column.jsx(row));
    }

    if (typeof column.mapper === 'function' && column.displayField) {
        const displayValue = getFieldValue(row, column.displayField);
        const mapped = column.mapper(displayValue);
        return mapped !== null && mapped !== undefined ? String(mapped) : '';
    }

    const rawValue = getFieldValue(row, column.field);
    return formatValue(rawValue, column);
};

const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const exportToXlsx = ({ items, columns, filename = 'export.xlsx', sheetName = 'Data' }) => {
    const exportColumns = columns.filter(col =>
        (col.field || col.exportValue || col.jsx) && col.display !== false
    );

    const headers = exportColumns.map(col => col.label || col.field);
    const rows = items.map(item =>
        exportColumns.map(col => getCellValue(item, col))
    );

    const sheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    worksheet['!cols'] = sheetData[0].map((_, colIndex) => {
        const maxLen = sheetData.reduce((max, row) => {
            const cellLen = row[colIndex] != null ? String(row[colIndex]).length : 0;
            return Math.max(max, cellLen);
        }, 0);
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    downloadFile(blob, filename);
};

/**
 * Fetch data from BE and export to XLSX.
 */
export const fetchAndExportToXlsx = async ({
                                               url,
                                               columns,
                                               filename = 'export.xlsx',
                                               filters = [],
                                               sort = 'created',
                                               order = 'DESC',
                                               query = null,
                                           }) => {
    const PAGE_SIZE = 1000;
    const allItems = [];
    let page = 0;

    while (true) {
        const params = constructQuery({
            query,
            filters,
            sort,
            order,
            page,
            pageSize: PAGE_SIZE,
        });

        const response = await fetchM(url, { params });
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.items || []);

        if (items.length === 0) break;
        allItems.push(...items);

        if (items.length < PAGE_SIZE) break;
        page++;
    }

    exportToXlsx({ items: allItems, columns, filename });
};

const ExportButtonInner = ({ url, columns, gridState, gridName, exportFileName, auth }) => {
    const [exporting, setExporting] = useState(false);

    if (!isInternal(auth)) return null;

    const handleExport = async () => {
        setExporting(true);
        try {
            await fetchAndExportToXlsx({
                url,
                columns,
                filters: gridState ? gridState.filters : [],
                sort: gridState ? gridState.sort : 'created',
                order: gridState ? gridState.order : 'DESC',
                query: gridState ? gridState.query : null,
                filename: `${exportFileName ? exportFileName : (gridName || 'export')}.xlsx`,
            });
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Button
            className="primary"
            onTouchTap={handleExport}
            disabled={exporting}
            style={{ color: 'white', marginRight: 10, padding: '0px 15px' }}
        >
            {exporting ? 'Exportování...' : 'Export do Excelu'}
        </Button>
    );
};

const mapStateToProps = (state, ownProps) => {
    const fullGridName = `${ownProps.gridName}_${getDecodedUser().sub || 'notLogged'}`;
    return {
        gridState: state.grid[fullGridName] || null,
        auth: state.sidebar ? state.sidebar.auth : null,
    };
};

export const ExportButton = connect(mapStateToProps)(ExportButtonInner);